/**
 * One-click checkout section — renders Apple Pay / Google Pay wallet buttons
 * on detail pages so users can purchase without going through the full checkout flow.
 *
 * Gated by the `enable_one_click_checkout` waffle flag (venue-scoped for A/B testing).
 *
 * Rendered in three locations:
 *  - Detail page modal footer (event/ticket dialog)
 *  - Attraction sidebar (desktop, via StickyTabs)
 *  - Mobile cart summary drawer (via CartSummaryFooter)
 *
 * When the flag is off but the basket is eligible, the Stripe element is still
 * mounted invisibly to detect wallet capability for A/B test logging. When the
 * basket is ineligible, fires a lightweight analytics event and returns null.
 */
import { useMemo, useCallback, useEffect, useRef } from "react";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { DataProvider } from "@shared/constants/common";
import {
  PaymentManagerProvider,
  PostPurchaseCallbackProps,
} from "@shared/modules/payments/contexts/PaymentManagerContext";
import WalletElement from "@shared/modules/payments/components/WalletElement/WalletElement.web";
import type { WalletSourceStep } from "@shared/modules/payments/components/WalletElement/WalletElement.props";
import ErrorBoundary from "@shared/components/application/other/ErrorBoundary/ErrorBoundary.web";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import { AnalyticsService } from "@shared/services/analytics";
import { useTranslation } from "@shared/utils/internationalization";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Divider from "@ui-kit/data-display/Divider/Divider.web";
import Portal from "@ui-kit/other/Portal/Portal.web";
import ProcessingLoaderContainer from "@app-contexts/public/shared/checkout/components/processing/ProcessingLoader/ProcessingLoaderContainer.web";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import {
  WAFFLE_FLAG_ONE_CLICK_CHECKOUT,
  WAFFLE_FLAG_ONE_CLICK_CHECKOUT_BYPASS_QUESTIONS,
} from "@shared/constants/waffle-flags";
import WebviewComponentAdaptor from "@shared/components/application/other/WebviewComponentAdaptor/WebviewComponentAdaptor.web";
import { isEligibleForExpressCheckout } from "../../utils/express-checkout-eligibility";
import {
  useExpressCheckout,
  ExpressCheckoutPurchaseState,
} from "../../hooks/useExpressCheckout";

export type { ExpressCheckoutPurchaseState };

interface ExpressCheckoutSectionProps {
  venueId?: number;
  sourceStep: WalletSourceStep;
  marginTop?: string;
  marginBottom?: string;
  onPurchaseStateChange?: (state: ExpressCheckoutPurchaseState) => void;
}

interface ExpressCheckoutInnerProps {
  eligible: boolean;
  showWallet: boolean;
  isOneClickCheckoutEnabled: boolean;
  venueId?: number;
  sourceStep: WalletSourceStep;
  marginTop?: string;
  marginBottom?: string;
  onPurchaseStateChange?: (state: ExpressCheckoutPurchaseState) => void;
}

function ExpressCheckoutInner({
  eligible,
  showWallet,
  isOneClickCheckoutEnabled,
  venueId,
  sourceStep,
  marginTop,
  marginBottom,
  onPurchaseStateChange,
}: ExpressCheckoutInnerProps): JSX.Element {
  const { t } = useTranslation("checkout");

  const {
    setIsWalletReady,
    isOverlayVisible,
    overlayMessage,
    visible,
    handlePaymentSuccess,
    handleWalletClick,
    handleWalletCancel,
  } = useExpressCheckout({
    eligible,
    venueId,
    onPurchaseStateChange,
  });

  const walletVisible = isOneClickCheckoutEnabled && visible;

  return (
    <>
      {isOverlayVisible && (
        <Portal>
          <ProcessingLoaderContainer
            message={overlayMessage}
            data-testid="express-checkout-processing"
          />
        </Portal>
      )}
      {showWallet && (
        <Box
          display={walletVisible ? "block" : "none"}
          data-testid="express-checkout-section"
          mb={marginBottom}
        >
          <Flex alignItems="center" gap="sm" mt={marginTop} mb="sm">
            <Divider flex={1} />
            <Text textStyle="heading.sm" color="fg.muted">
              {t("Express checkout")}
            </Text>
            <Divider flex={1} />
          </Flex>
          <Box minHeight="element.4xl">
            <ErrorBoundary fallback={null}>
              <WalletElement
                sourceStep={sourceStep}
                extraAnalyticsParams={{
                  one_click_checkout_eligible: eligible,
                  one_click_checkout_enabled: isOneClickCheckoutEnabled,
                }}
                onElementReady={() => setIsWalletReady(true)}
                onElementError={() => setIsWalletReady(false)}
                onComponentUnmount={() => setIsWalletReady(false)}
                onPaymentSuccess={handlePaymentSuccess}
                onWalletClick={handleWalletClick}
                onWalletCancel={handleWalletCancel}
              />
            </ErrorBoundary>
          </Box>
        </Box>
      )}
    </>
  );
}

function handlePostPurchase({
  finalBasket,
  invoice,
}: PostPurchaseCallbackProps): void {
  AnalyticsService.postPurchase(finalBasket, invoice, {
    one_click_checkout_used: true,
  });
}

export function ExpressCheckoutSection({
  venueId,
  sourceStep,
  marginTop,
  marginBottom = "none",
  onPurchaseStateChange,
}: ExpressCheckoutSectionProps): JSX.Element | null {
  const { isLoggedIn } = useUserSessionAuthState();
  const publicBasket = usePublicBasket();
  const { itemGroups, hasItems, isFree, customInfoQuestions, isWaitlist } =
    publicBasket;
  const purchaseStartedRef = useRef(false);
  const { isFlagActive } = useDjangoWaffle();

  const isOneClickCheckoutEnabled = isFlagActive(
    WAFFLE_FLAG_ONE_CLICK_CHECKOUT,
    venueId
  );

  const bypassCustomQuestions = isFlagActive(
    WAFFLE_FLAG_ONE_CLICK_CHECKOUT_BYPASS_QUESTIONS,
    venueId
  );

  const eligible = useMemo(
    () =>
      isEligibleForExpressCheckout({
        itemGroups,
        basketCustomQuestions: customInfoQuestions,
        bypassCustomQuestions,
        isWaitlist,
      }),
    [itemGroups, customInfoQuestions, bypassCustomQuestions, isWaitlist]
  );

  const handlePurchaseStateChange = useCallback(
    (state: ExpressCheckoutPurchaseState) => {
      if (
        state === "wallet_active" ||
        state === "processing" ||
        state === "complete"
      ) {
        purchaseStartedRef.current = true;
      } else if (state === "idle") {
        purchaseStartedRef.current = false;
      }
      onPurchaseStateChange?.(state);
    },
    [onPurchaseStateChange]
  );

  useEffect(() => {
    if (!hasItems) return;
    if (eligible) return;

    AnalyticsService.buttonClick({
      label: "wallet_exposure",
      venue_id: venueId,
      custom_params: {
        venue_id: venueId,
        source_step: sourceStep,
        one_click_checkout_eligible: false,
        one_click_checkout_enabled: isOneClickCheckoutEnabled,
        wallet_shown: false,
        not_shown_reason: "basket_ineligible",
      },
    });
  }, [isOneClickCheckoutEnabled, hasItems, eligible, venueId, sourceStep]);

  // Keep the component mounted during purchase so the processing overlay persists
  // until the redirect completes. Without this, emptyBasket() causes eligible/hasItems
  // to flip false, unmounting the overlay and flashing the underlying page.
  if (!purchaseStartedRef.current) {
    if (!eligible) return null;
    if (!hasItems || isFree) return null;
  }

  const basketType =
    publicBasket?.userId != null && isLoggedIn
      ? DataProvider.User
      : DataProvider.Public;

  return (
    <WebviewComponentAdaptor
      webComponent={
        <PaymentManagerProvider
          config={{
            walletsEnabled: true,
            isLoggedIn,
          }}
          basket={publicBasket}
          basketType={basketType}
          onPostPurchase={handlePostPurchase}
        >
          <ExpressCheckoutInner
            sourceStep={sourceStep}
            marginTop={marginTop}
            marginBottom={marginBottom}
            eligible={eligible}
            isOneClickCheckoutEnabled={isOneClickCheckoutEnabled}
            showWallet={hasItems && !isFree}
            venueId={venueId}
            onPurchaseStateChange={handlePurchaseStateChange}
          />
        </PaymentManagerProvider>
      }
      // In react native webview, do not show express checkout.
      // Apple Pay and Google Pay do not work properly with react-native-webview
      webviewComponent={null}
    />
  );
}
